// Tags de sécurité
import Vue from 'vue';
import axios from 'axios';
import FingerPrint from 'fingerprintjs2';

const csJWTTAG = 'x-jwt-ael';
const csFINGERPRINTTAG = 'x-fingerprint';
const csTABIDTAG = 'x-tab';
const csPARENTTAG = 'x-parent';
const csBROWSERTAG = 'x-browserid';

// code d'erreur
const ciACCESSDENIED = 406;
const CESSCONFLICT = 409;

export default class HttpApi {
  static coAxios = null;

  static csAuthUrl = '/auth';

  static csRefreshUrl = '/auth/refreshUser';

  msUrlAskToken = '';

  /**
   * Constructeur
   * @param fctWaiting fonction pour gérer le sablier
   */
  constructor(fctWaiting) {
    this.initToken();
    this.createAxios(fctWaiting);
  }

  // eslint-disable-next-line class-methods-use-this
  static async getFingerPrint() {
    let bNew = false;
    let sBrowserUniqueId = HttpApi.getToken(csBROWSERTAG, true);

    // To made this browser unique, generate a random ID stored in local storage (in order to be persistent after browser close/reopen)
    if (HttpApi.isNotValidToken(sBrowserUniqueId, false)) {
      sBrowserUniqueId = FingerPrint.x64hash128(Math.floor(Math.random() * 80), 31);
      HttpApi.saveToken(csBROWSERTAG, sBrowserUniqueId, true);
      bNew = true;
    }
    let sFingerPrint = HttpApi.getToken(csFINGERPRINTTAG, false);
    if (HttpApi.isNotValidToken(sFingerPrint, false) || bNew) {
      const listOption = {
        fonts: { extendedJsFonts: true },
        excludes: { language: true },
      };
      sFingerPrint = await FingerPrint.getPromise(listOption).then((inlistComponent) => {
        const listValue = inlistComponent.map((component) => component.value);

        // Add this ID because, in Enterprise, most of the time browser have the same configuration
        listValue.push(sBrowserUniqueId);
        sFingerPrint = FingerPrint.x64hash128(listValue.join(''), 31);
        HttpApi.saveToken(csFINGERPRINTTAG, sFingerPrint, false);
        return sFingerPrint;
      });
    }
    return sFingerPrint;
  }

  /**
   * Creation de la partie axios
   * @param fctWaiting Fonction pour la mire d'attente
   * @return Axios instance
   */
  createAxios(fctWaiting) {
    if (HttpApi.coAxios !== null) {
      this.axiosInstance = HttpApi.coAxios;
      return this.axiosInstance;
    }
    HttpApi.coAxios = axios.create({
      baseURL: '/afcael2pub/backend/rest',
      retry: false,
    });
    this.axiosInstance = HttpApi.coAxios;
    this.accessToken = 0;
    this.addRequestInterceptor(fctWaiting);
    this.addAnswerInterceptor(fctWaiting);
    return this.axiosInstance;
  }

  /**
   * Ajoute l'intercepteur sur la requête
   * @param fctWaiting Fonction pour la mire d'attente
   * @return Axios instance
   */
  addRequestInterceptor(fctWaiting) {
    this.axiosInstance.interceptors.request.use(
      (inoRequest) => {
        fctWaiting(true);
        HttpApi.addHeader(inoRequest); // on ajoute les tokens liés à JWT
        return inoRequest;
      },
      (inexError) => {
        fctWaiting(false);
        return Promise.reject(inexError);
      },
    );
  }

  /**
   * Ajoute l'intercepteur sur la réponse
   * @param callback Fonction pour la mire d'attente
   * @return Axios instance
   */
  addAnswerInterceptor(callback) {
    const self = this;

    this.axiosInstance.interceptors.response.use(
      (inoResponse) => { // réponse Ok
        callback(false);
        HttpApi.saveTokenFromResponse(inoResponse);
        return inoResponse;
      },
      (inexError) => { // en cas d'erreur
        callback(false);
        const sError = inexError.response;
        const iStatus = sError ? sError.status : 0; // lecture protégée
        const coOriginalRequest = inexError.config;

        if (sError && HttpApi.isAccessConflict(iStatus) && coOriginalRequest && !coOriginalRequest.retry) { // call Jwt et relancer la request
          return self.sendJwtRequest(HttpApi.getUrlFromNewToken(), coOriginalRequest); // Demande un nouveau token jwt
        }
        // Access denied 406 -> il faut un jeton Jwt valide et pas avoir déjà appeler le service Jwt
        if (sError && HttpApi.isAccessDenied(iStatus) && coOriginalRequest && !coOriginalRequest.retry) { // call Jwt et relancer la request
          let sUrl = HttpApi.csRefreshUrl; // Demande un refresh du token jwt

          if (HttpApi.isNotValidToken()) { // Pas encore de token ou token non rafraichi
            if (self.msUrlAskToken === coOriginalRequest.url) { // Token déjà demandé
              self.msUrlAskToken = '';
              return Promise.reject(inexError);
            }
            sUrl = HttpApi.getUrlFromNewToken(); // Demande un nouveau token jwt
            self.msUrlAskToken = coOriginalRequest.url;
          }
          return self.sendJwtRequest(sUrl, coOriginalRequest); // Envoie la requête liée à Jwt
        }
        return Promise.reject(inexError);
      },
    );
  }

  /**
   * Initialisation des tokens fixes (Browser, onglet)
   */
  // eslint-disable-next-line class-methods-use-this
  async initToken() {
    let iFound = 0;
    if (HttpApi.getToken(csBROWSERTAG, true)) {
      iFound += 1;
    }
    if (HttpApi.getToken(csFINGERPRINTTAG, false)) { // getFingerprint est asynchrone
      iFound += 1;
    }
    let sToken = HttpApi.getToken(csJWTTAG, false);
    if (!HttpApi.isNotValidToken(sToken, false)) {
      // communication avec le serveur déjà initiée
      iFound += 1;
    }
    sToken = HttpApi.getToken(csTABIDTAG, false);
    iFound += 1;
    if (HttpApi.isNotValidToken(sToken, false)) {
      // in order to made this browser tab unique, generate a random ID that we will store in session storage
      HttpApi.saveToken(csTABIDTAG, Date.now(), false); // id pour l'onglet
    }
    return iFound;
  }

  /**
   * Demande un nouveau token au serveur
   */
  createToken() {
    this.sendJwtRequest(HttpApi.getUrlFromNewToken());
    return this.accessToken;
  }

  /**
   * Redemande les autorisations au serveur
   * @param insUrl Nouvelle autorisation ou refresh
   * @param inoOriginalRequest Requête initiale à rejouer avec les nouvelles autorisations
   * @return {Promise<any>}
   */
  sendJwtRequest(insUrl, inoOriginalRequest) {
    const self = this;

    return new Promise((resolve, reject) => {
      self.accessToken = 1; // demande en cours
      self.axiosInstance.get(insUrl).then((inoReponse) => {
        const sJwt = inoReponse.data.jwtToken;
        const oOriginalRequest = inoOriginalRequest;

        if (!HttpApi.isNotValidToken(sJwt, true)) { // todo true
          HttpApi.saveToken(csJWTTAG, sJwt, false);
          self.accessToken = 2; // demande en terminée
          if (oOriginalRequest) {
            HttpApi.addHeader(oOriginalRequest);
            oOriginalRequest.retry = true; // Relance la requete
            // oOriginalRequest.baseURL = ''; // Evite de remettre la base url
            return resolve(self.axiosInstance(inoOriginalRequest));
          }
          return resolve(inoReponse);
        }
        self.accessToken = 0; // permet de redemander
        return reject('Request error : no valid jwt');  // eslint-disable-line
      }).catch((inexBad) => {
        self.accessToken = 0; // permet de redemander
        return reject(inexBad);
      });
    });
  }

  /**
   * Retourne l'Url pour demander un nouveau token
   * @return {string} Url
   */
  static getUrlFromNewToken() {
    HttpApi.deleteToken(csJWTTAG, false);
    return HttpApi.csAuthUrl;
  }

  /**
   * Retourne l'instance courante axios pour exécuter les appels ajax
   * @return {AxiosInstance}
   */
  static getAxios() { return HttpApi.coAxios; }

  /**
   * Ajoute les tokens dans l'entête de la la requête ajax
   * @param inoRequest Requête courante
   */
  static addHeader(inoRequest) {
    const oRequest = inoRequest;
    let sToken = HttpApi.getToken(csJWTTAG, false);

    if (!HttpApi.isNotValidToken(sToken, false)) oRequest.headers[csJWTTAG] = sToken;

    sToken = HttpApi.getToken(csPARENTTAG, false);
    if (!HttpApi.isNotValidToken(sToken, false)) oRequest.headers[csPARENTTAG] = sToken;

    sToken = HttpApi.getToken(csFINGERPRINTTAG, false);
    if (!HttpApi.isNotValidToken(sToken, false)) oRequest.headers[csFINGERPRINTTAG] = sToken;

    // Pour csTABIDTAG, meme principe que ds le initToken
    sToken = HttpApi.getToken(csTABIDTAG, false);
    if (HttpApi.isNotValidToken(sToken, false)) {
      // in order to made this browser tab unique, generate a random ID that we will store in session storage
      HttpApi.saveToken(csTABIDTAG, Date.now(), false); // id pour l'onglet
      sToken = HttpApi.getToken(csTABIDTAG, false);
    }
    oRequest.headers[csTABIDTAG] = sToken;

    oRequest.headers.Accept = 'application/json, application/octet-stream, application/pdf';
    oRequest.headers['Access-Control-Expose-Headers'] = `${csJWTTAG},${csFINGERPRINTTAG},${csTABIDTAG},${csPARENTTAG}`;
  }

  /**
   * Retourne le token demandé
   * @param insTag Token à retourner
   * @param inbLocal True -> gestion local
   * @return {string} Valeur du token
   */
  static getToken(insTag, inbLocal) {
    const oStorage = inbLocal ? localStorage : sessionStorage;

    return oStorage.getItem(insTag);
  }

  /**
   * Effacement du token demandé
   * @param insTag Token à enlever
   * @param inbLocal True -> gestion local
   */
  static deleteToken(insTag, inbLocal) {
    const oStorage = inbLocal ? localStorage : sessionStorage;

    return oStorage.removeItem(insTag);
  }

  /**
   * Sauve la valeur du token
   * @param insTag Token à sauver
   * @param insValue Valeur à mémoriser
   * @param inbLocal True -> gestion local
   */
  static saveToken(insTag, insValue, inbLocal) {
    const oStorage = inbLocal ? localStorage : sessionStorage;

    oStorage.setItem(insTag, insValue);
  }

  static isAccessConflict(iniStatus) { return iniStatus === CESSCONFLICT; }

  static isAccessDenied(iniStatus) { return iniStatus === ciACCESSDENIED; }

  /**
   * Teste si le jeton est invalide
   * @param insToken Jeton à controller
   * @param inbIsJwt True pour controler l'expire
   * @returns {boolean}
   */
  static isNotValidToken(insToken, inbIsJwt) {
    if (inbIsJwt && insToken) {
      try {
        const sBase64Payload = insToken.split('.')[1];
        const sBase64 = sBase64Payload.replace('-', '+').replace('_', '/');
        const sTokenPayload = JSON.parse(window.atob(sBase64));
        const iExpirationTime = sTokenPayload.exp * 1000;
        // on laisse 30 secondes de marge
        return Date.now() + 30000 > iExpirationTime;
      } catch (err) {
        return false;
      }
    }
    return (insToken === null || insToken === undefined || insToken === 'undefined');
  }

  /**
   * Sauve la valeur du token jwt issue de la réponse du serveur
   * @param inoResponse Réponse courante
   */
  static saveTokenFromResponse(inoResponse) {
    const sValue = (inoResponse.data && inoResponse.data.jwtToken) ? inoResponse.data.jwtToken : inoResponse.headers[csJWTTAG];

    HttpApi.saveToken(csJWTTAG, sValue);
  }
}
